/**
 * Team sizing and completeness (PRD §4.7 step 7), shared by the API (status, "Team complete"
 * email, seats a captain may still give) and the profile ("1 / 2 membres"). Pure, so the rules
 * are unit-tested without a database.
 */
export type TeamSeatStatus =
  'invited' | 'joined' | 'replaced' | 'refunded' | 'superseded' | 'moved' | 'cancelled';
export type TeamSeat = { status: TeamSeatStatus };

/** Seats that still count: a pending invitation or a paid member. Dead seats are history. */
export const isLiveSeat = (m: TeamSeat) => m.status === 'invited' || m.status === 'joined';

export type TeamProgress = {
  /** Members who paid their place (the captain included). */
  joined: number;
  /** Invitations not paid yet. */
  invited: number;
  sizeMin: number;
  /** Null = unlimited (record 0021). */
  sizeMax: number | null;
  /**
   * Seats the captain can still give out: pending invitations are held until paid or replaced.
   * Null when the team size is unlimited.
   */
  seatsLeft: number | null;
  /** Enough members paid to race; more may still join up to `sizeMax`. */
  complete: boolean;
  /** No more paid member fits (`sizeMax` reached). */
  full: boolean;
};

export function teamProgress(
  format: { teamSizeMin: number | null; teamSizeMax: number | null },
  members: TeamSeat[],
): TeamProgress {
  const joined = members.filter((m) => m.status === 'joined').length;
  const invited = members.filter((m) => m.status === 'invited').length;
  const sizeMin = format.teamSizeMin ?? 1;
  const sizeMax = format.teamSizeMax;
  return {
    joined,
    invited,
    sizeMin,
    sizeMax,
    seatsLeft: sizeMax === null ? null : Math.max(0, sizeMax - joined - invited),
    complete: joined >= sizeMin,
    full: sizeMax !== null && joined >= sizeMax,
  };
}

/** Teammate email fields to show the captain: the seats left, at most `max` at once; `max` when unlimited. */
export function teammateFieldCount(seatsLeft: number | null, max = 3): number {
  return seatsLeft === null ? max : Math.max(0, Math.min(seatsLeft, max));
}
